export type AcademyRole = "admin" | "funcionario" | "caixa" | "recepcionista";

export type AcademyLesson = {
  id: string;
  title: string;
  description: string;
  actionLabel: string;
  path: string;
  xp: number;
  roles: AcademyRole[];
};

export type AcademyTrail = {
  id: string;
  title: string;
  description: string;
  emoji: string;
  color: string;
  lessons: AcademyLesson[];
};

export const ACADEMY_TRAILS: AcademyTrail[] = [
  {
    id: "primeiros-passos",
    title: "Primeiros passos",
    description: "Prepare o salão para começar a operar com segurança.",
    emoji: "🚀",
    color: "from-amber-500 to-orange-600",
    lessons: [
      { id: "welcome-dashboard", title: "Conheça o painel", description: "Encontre agenda, pendências e atalhos da operação.", actionLabel: "Abrir painel", path: "/", xp: 20, roles: ["admin", "funcionario", "caixa", "recepcionista"] },
      { id: "setup-salon", title: "Configure o salão", description: "Complete dados, horários e identidade visual.", actionLabel: "Abrir configurações", path: "/settings", xp: 30, roles: ["admin"] },
      { id: "setup-services", title: "Cadastre serviços", description: "Defina preço, duração e especialidades.", actionLabel: "Abrir serviços", path: "/services", xp: 30, roles: ["admin", "funcionario"] },
      { id: "setup-team", title: "Organize a equipe", description: "Cadastre profissionais e libere os acessos corretos.", actionLabel: "Abrir equipe", path: "/staff", xp: 30, roles: ["admin"] },
    ],
  },
  {
    id: "operacao-diaria",
    title: "Operação sem complicação",
    description: "Domine agenda, atendimento e fila do dia.",
    emoji: "📅",
    color: "from-blue-500 to-indigo-600",
    lessons: [
      { id: "agenda-weekly", title: "Organize a agenda", description: "Crie, confirme e reagende atendimentos.", actionLabel: "Abrir agenda", path: "/weekly-calendar", xp: 30, roles: ["admin", "funcionario", "recepcionista"] },
      { id: "walk-in", title: "Atenda sem agendamento", description: "Registre rapidamente quem chegou ao salão.", actionLabel: "Abrir agendamentos", path: "/appointments", xp: 25, roles: ["admin", "funcionario", "recepcionista"] },
      { id: "queue", title: "Conduza a fila", description: "Organize chamadas e o fluxo da recepção.", actionLabel: "Abrir Caixa", path: "/checkout", xp: 25, roles: ["admin", "caixa", "recepcionista"] },
    ],
  },
  {
    id: "caixa-comissoes",
    title: "Caixa e comissões",
    description: "Receba, confira e acompanhe os resultados financeiros.",
    emoji: "💰",
    color: "from-emerald-500 to-teal-600",
    lessons: [
      { id: "checkout", title: "Receba no Caixa", description: "Registre pagamentos, troco e múltiplas formas de recebimento.", actionLabel: "Abrir Caixa", path: "/checkout", xp: 30, roles: ["admin", "caixa", "recepcionista"] },
      { id: "standalone-sales", title: "Registre venda avulsa", description: "Venda produtos ou lance consumo de profissionais.", actionLabel: "Abrir venda avulsa", path: "/checkout", xp: 25, roles: ["admin", "caixa", "recepcionista"] },
      { id: "commissions", title: "Feche comissões", description: "Confira regras, débitos e pagamentos da equipe.", actionLabel: "Abrir comissões", path: "/commissions", xp: 30, roles: ["admin"] },
      { id: "products", title: "Controle produtos e estoque", description: "Cadastre itens, acompanhe reposições e registre usos internos.", actionLabel: "Abrir produtos", path: "/products", xp: 25, roles: ["admin", "caixa"] },
      { id: "financial", title: "Organize o financeiro", description: "Acompanhe contas a pagar, receber e o fluxo do salão.", actionLabel: "Abrir financeiro", path: "/financial", xp: 25, roles: ["admin"] },
    ],
  },
  {
    id: "clientes-crescimento",
    title: "Clientes que voltam",
    description: "Use CRM, pacotes e promoções para aumentar a recorrência.",
    emoji: "💛",
    color: "from-pink-500 to-rose-600",
    lessons: [
      { id: "clients", title: "Cuide da base de clientes", description: "Cadastre, consulte histórico e mantenha dados atualizados.", actionLabel: "Abrir clientes", path: "/clients", xp: 25, roles: ["admin"] },
      { id: "crm", title: "Ative o CRM", description: "Crie segmentos e campanhas com consentimento.", actionLabel: "Abrir CRM", path: "/crm", xp: 25, roles: ["admin"] },
      { id: "loyalty", title: "Crie recorrência", description: "Use pacotes e fidelidade para valorizar clientes frequentes.", actionLabel: "Abrir fidelidade", path: "/loyalty", xp: 25, roles: ["admin"] },
      { id: "promotions", title: "Promova com estratégia", description: "Monte promoções e acompanhe oportunidades de venda.", actionLabel: "Abrir promoções", path: "/promotions", xp: 25, roles: ["admin"] },
      { id: "public-booking", title: "Ative a reserva pública", description: "Revise a jornada online e o sinal de confirmação.", actionLabel: "Abrir configurações", path: "/settings", xp: 25, roles: ["admin"] },
    ],
  },
  {
    id: "ia-gestao",
    title: "IA e gestão inteligente",
    description: "Conecte canais e transforme informações em decisões.",
    emoji: "✨",
    color: "from-violet-500 to-purple-600",
    lessons: [
      { id: "ai-conversations", title: "Acompanhe a IA", description: "Priorize conversas e assuma atendimentos quando necessário.", actionLabel: "Abrir Central", path: "/ai-conversations", xp: 30, roles: ["admin"] },
      { id: "knowledge", title: "Ensine a IA", description: "Organize a base de conhecimento para respostas mais confiáveis.", actionLabel: "Abrir conhecimento", path: "/knowledge", xp: 25, roles: ["admin"] },
      { id: "media", title: "Comunique no Painel de TV", description: "Use mídia promocional, campanhas e conteúdo visual na recepção.", actionLabel: "Abrir mídia", path: "/media", xp: 25, roles: ["admin"] },
      { id: "intelligence", title: "Leia os indicadores", description: "Acompanhe metas, alertas e oportunidades do salão.", actionLabel: "Abrir inteligência", path: "/intelligence", xp: 30, roles: ["admin"] },
      { id: "reports", title: "Decida com relatórios", description: "Analise receita, serviços e desempenho da equipe.", actionLabel: "Abrir relatórios", path: "/reports", xp: 25, roles: ["admin"] },
    ],
  },
  {
    id: "gestao-segura",
    title: "Gestão e segurança",
    description: "Finalize a formação com os controles que deixam o negócio preparado para crescer.",
    emoji: "🛡️",
    color: "from-slate-700 to-slate-950",
    lessons: [
      { id: "team-access", title: "Defina acessos da equipe", description: "Organize permissões, vínculos e funções de cada pessoa.", actionLabel: "Abrir equipe", path: "/staff", xp: 25, roles: ["admin"] },
      { id: "branches", title: "Gerencie filiais", description: "Acompanhe unidades, profissionais e comparativos entre operações.", actionLabel: "Abrir filiais", path: "/branches", xp: 25, roles: ["admin"] },
      { id: "integrations", title: "Conecte os canais", description: "Revise WhatsApp, Instagram e demais integrações do salão.", actionLabel: "Abrir integrações", path: "/integrations", xp: 25, roles: ["admin"] },
      { id: "privacy", title: "Proteja os dados", description: "Use privacidade, consentimentos e auditoria com responsabilidade.", actionLabel: "Abrir segurança", path: "/privacy-security", xp: 25, roles: ["admin"] },
    ],
  },
];

export const ACADEMY_LESSONS = ACADEMY_TRAILS.flatMap((trail) => trail.lessons.map((lesson) => ({ ...lesson, trailId: trail.id })));

export const ACADEMY_TOTAL_XP = ACADEMY_LESSONS.reduce((sum, lesson) => sum + lesson.xp, 0);

export function getAcademyLessonsForRole(role: AcademyRole) {
  return ACADEMY_LESSONS.filter((lesson) => lesson.roles.includes(role));
}

export function getAcademyTrailForLesson(lessonId: string) {
  return ACADEMY_TRAILS.find((trail) => trail.lessons.some((lesson) => lesson.id === lessonId));
}

export const ACADEMY_ACHIEVEMENTS = [
  { id: "start", title: "Primeiro passo", description: "Conclua sua primeira missão.", threshold: 1, emoji: "🌱" },
  { id: "explorer", title: "Explorador", description: "Conclua 5 missões.", threshold: 5, emoji: "🧭" },
  { id: "operator", title: "Operação em dia", description: "Conclua 10 missões.", threshold: 10, emoji: "⚡" },
  { id: "graduate", title: "Especialista B&B", description: "Conclua todas as missões do seu perfil.", threshold: Number.MAX_SAFE_INTEGER, emoji: "🏆" },
] as const;
